// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Instant parsing and elapsed math, all timezone-independent. Anything that
// renders a time for a person lives in siteTime.ts.

/**
 * Parse an ISO timestamp, treating an offset-less string as UTC.
 *
 * AEM serves `event_timestamp` straight out of Fabric SQL with no offset, and
 * bare `new Date('2026-08-24T13:21:00')` reads that as *local* — which printed
 * the raw UTC clock on every card and made every "… ago" label negative (then
 * clamped to 0:00). Anything already carrying `Z` or `±HH:MM` is left alone.
 */
export function parseInstant(iso: string): Date {
  return new Date(/(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
}

/**
 * ISO timestamp one calendar month before now, e.g. for "created in the last
 * month" filters.
 *
 * The day of month is clamped to the target month's length. A bare
 * `setMonth(m - 1)` overflows when the previous month is shorter — on Mar 31 it
 * lands on Mar 3, silently shrinking the window to three days — so the month is
 * moved from the 1st and the day restored afterwards. Time of day is preserved.
 */
export function oneMonthAgoIso(now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(now.getDate(), lastDayOfMonth));
  return d.toISOString();
}

/**
 * Whole minutes elapsed since an ISO timestamp, for "raised … ago" labels.
 * Returns undefined when the input is absent or unparseable, and clamps to 0 so
 * a slightly-future timestamp (clock skew) never reads as negative.
 */
export function minutesSince(
  iso: string | null | undefined,
  now: Date = new Date()
): number | undefined {
  if (!iso) return undefined;
  const then = parseInstant(iso).getTime();
  if (Number.isNaN(then)) return undefined;
  return Math.max(0, Math.floor((now.getTime() - then) / 60000));
}

/**
 * Whole seconds elapsed between two ISO timestamps (`to` minus `from`).
 * Returns undefined when either input is absent or unparseable. Unlike
 * `minutesSince`, this does not clamp to 0 — a negative result (e.g. an
 * acknowledgment timestamp that precedes the raise timestamp, from clock
 * skew or bad data) is left visible rather than hidden, since silently
 * flooring it to 0 would misrepresent an SLA as met.
 */
export function secondsBetween(
  from: string | null | undefined,
  to: string | null | undefined
): number | undefined {
  if (!from || !to) return undefined;
  const fromMs = parseInstant(from).getTime();
  const toMs = parseInstant(to).getTime();
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return undefined;
  return Math.floor((toMs - fromMs) / 1000);
}

/**
 * `total` (in `unitSeconds`-sized units) as a "big:ss" string — the shared
 * shape behind both "elapsed" labels (minutes → h:mm) and SLA countdowns
 * (seconds → m:ss). Negative input clamps to 0 so a slightly-future
 * timestamp (clock skew) never renders as negative.
 */
function formatBigColonSmall(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const big = Math.floor(safe / 60);
  const small = safe % 60;
  return `${big}:${('0' + small).slice(-2)}`;
}

const MINUTES_PER_DAY = 24 * 60;

/**
 * Minutes elapsed → label. Under a day it is "H:MM", e.g. 95 minutes → "1:35"
 * (1h 35m ago). From a day on it rolls over to whole days, e.g. 59,868 minutes
 * → "41d", because "997:48" is not a readable age (AB#36209).
 */
export function formatElapsedMinutes(totalMinutes: number): string {
  const safe = Math.max(0, Math.floor(totalMinutes));
  if (safe >= MINUTES_PER_DAY) return `${Math.floor(safe / MINUTES_PER_DAY)}d`;
  return formatBigColonSmall(safe);
}

/** Seconds → "M:SS" label, e.g. 30 seconds → "0:30" (an SLA ack budget). */
export function formatSeconds(totalSeconds: number): string {
  return formatBigColonSmall(totalSeconds);
}

export function oneMonthAgoStartOfDayIso(now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(now.getDate(), lastDayOfMonth));
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * Parse a "HH:MM" string to minutes-since-midnight for chronological sorting.
 * Returns -1 for anything that isn't HH:MM so malformed values sort last.
 */
export function hhmmToMinutes(t: string): number {
  const m = t.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return -1;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return -1;
  return hours * 60 + minutes;
}

/** ISO timestamp of the start (00:00:00.000, local time) of the day `d` falls on. */
export function startOfDayIso(d: Date): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
}

/** ISO timestamp of the end (23:59:59.999, local time) of the day `d` falls on. */
export function endOfDayIso(d: Date): string {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
}

/** A calendar day as `YYYY-MM-DD` in the browser's local time, for a URL. */
export function toDateParam(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** The local day for a `YYYY-MM-DD` URL value, or null when it isn't a real date. */
export function parseDateParam(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d);
  // new Date rolls 2026-02-31 over to March: only a day that round-trips is real.
  return date.getFullYear() === y && date.getMonth() === mo - 1 && date.getDate() === d
    ? date
    : null;
}
